import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  convertNumberToFrontend,
  convertAmountToFrontend,
} from "../../../helpers/convert";
import {
  getMarketProcessesListRequest,
  getGeneralOperationsListRequest,
  clearGeneralOperationsList,
  setChartTerm,
  getChartDataRequest,
  getOrganizedMarketInfoRequest,
  getSystemMsgListRequest,
  getHomeObjectionsListRequest,
} from "../../../redux/actions/homepage";

import {
  getSimplifiedSessionsRequested,
  getSessionRequested,
} from "../../../redux/actions/sessions";

import { Bar } from "react-chartjs-2";
import DataTable from "../../../components/DataTable";
import DataTableBasic from "../../../components/DataTable/DataTableBasic";
import { Select } from "../../../components/Inputs";
import Pill from "../../../components/Pill";
import ToggleSwitch from "../../../components/ToggleSwitch";
import { EmptyState } from "../../../components/EmptyState";
import Loading from "../../../components/Loading";
import {
  processNames,
  operationHistoryTrans,
} from "../../../constants/translations";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";

const Homepage = ({ t, history }) => {
  const createGeneralOperationsColumns = (
    timeTitle,
    isRedemption,
    isBilateral
  ) => {
    const organizationColumn = isBilateral
      ? [
          {
            name: t("pages.default.bilateralAgreements.table.seller"),
            selector: "fromOrganizationName",
            sortable: true,
            showTitle: true,
          },
          {
            name: t("pages.default.bilateralAgreements.table.buyer"),
            selector: "toOrganizationName",
            sortable: true,
            showTitle: true,
          },
        ]
      : isRedemption
      ? [
          {
            name: t("pages.default.certificates.table.organizationName"),
            selector: "organizationName",
            sortable: true,
            showTitle: true,
          },
          {
            name: t("pages.default.redemption.table.beneficiaryName"),
            selector: "beneficiaryName",
            sortable: true,
            cell: (row) => row.beneficiaryParty && row.beneficiaryParty.title,
          },
        ]
      : [
          {
            name: t("pages.default.certificates.table.organizationName"),
            selector: "organizationName",
            sortable: true,
            showTitle: true,
          },
        ];

    return [
      ...organizationColumn,
      {
        name: t("pages.default.homepage.table.user"),
        selector: "userName",
        sortable: true,
      },
      {
        name: t("pages.default.homepage.table.time"),
        selector: "createDate",
        sortable: true,
        cell: (row) => moment(row[timeTitle]).format("DD.MM.YYYY HH:mm:ss"),
      },
    ];
  };

  const generalOperationsTabs = [
    {
      title: t("pages.default.homepage.tabs.export"),
      columns: createGeneralOperationsColumns("createDate"),
      notFound: t("pages.default.homepage.exportNotFound"),
      loading: t("pages.default.homepage.exportLoading"),
      onClick: () =>
        dispatch(
          getGeneralOperationsListRequest("certificate", {
            certificateStatus: ["ACTIVE", "BLOCK"],
            certificateSources: ["EXPORT"],
          })
        ),
    },
    {
      title: t("pages.default.homepage.tabs.redemption"),
      columns: createGeneralOperationsColumns("redemptionDate", true, false),
      notFound: t("pages.default.homepage.redemptionNotFound"),
      loading: t("pages.default.homepage.redemptionLoading"),
      onClick: () =>
        dispatch(
          getGeneralOperationsListRequest("certificate/redemption", {
            redemptionStatus: ["INITIAL", "COMPLETED"],
          })
        ),
    },
    {
      title: t("pages.default.homepage.tabs.bilateralAgreement"),
      columns: createGeneralOperationsColumns("createdDate", false, true),
      notFound: t("pages.default.homepage.bilateralAgreementNotFound"),
      loading: t("pages.default.homepage.bilateralAgreementLoading"),
      onClick: () =>
        dispatch(
          getGeneralOperationsListRequest("bilateralAgreement", {
            status: ["INITIAL", "AGREEMENT", "SUCCESSFUL"],
          })
        ),
    },
  ];

  const dispatch = useDispatch();
  const { listProcess, loadingProcess } = useSelector(
    (state) => state.homepage.marketProcesses
  );
  const { list, loadingGO } = useSelector(
    (state) => state.homepage.generalOperations
  );
  const { year, month, data, loadingChart } = useSelector(
    (state) => state.homepage.chart
  );
  const { loadingInfo } = useSelector(
    (state) => state.homepage.organizedMarketInfo
  );
  const { listMsg, activePage, totalPageCount, loadingMsg } = useSelector(
    (state) => state.homepage.systemMsg
  );
  const { listObjections, loadingObjections } = useSelector(
    (state) => state.homepage.objections
  );

  const sessions = useSelector((state) => state.sessions.list);
  const currentContracts = useSelector(
    (state) => state.sessions.selectedSessionContracts
  );
  const [selectedTab, setSelectedTab] = useState(generalOperationsTabs[0]);
  const [filterPeriodOptions, setFilterPeriodOptions] = useState([]);
  const [totalMatchedAmount, setTotalMatchedAmount] = useState(0);
  const [sortedColumn, setSortedColumn] = useState("createDate");
  const [sortDirection, setSortDirection] = useState("desc");

  const size = 5;
  const fieldNames = {
    createDate: "CREATE_DATE",
    organizationName: "ORGANIZATION_NAME",
    userName: "USER_NAME",
    auditImportanceType: "AUDIT_IMPORTANCE_TYPE",
    auditorType: "AUDITOR_TYPE",
    auditType: "AUDIT_TYPE",
    description: "DESCRIPTION",
  };
  const processTranslations = processNames(t);
  const translations = operationHistoryTrans(t);
  const yekgProcessStatusTypes = {
    OPEN: "success",
    CLOSED: "important",
    WAITING: "default",
  };

  const marketProcessesColumns = [
    {
      name: t("pages.default.homepage.table.processes"),
      selector: "processName",
      sortable: false,
      cell: (row) => processTranslations[row.processName],
    },
    {
      name: t("pages.default.homepage.table.startTime"),
      selector: "startHour",
      sortable: true,
      cell: (row) => (
        <div>
          {row.startHour.toString().length === 1
            ? `0${row.startHour}`
            : row.startHour}
          :
          {row.startMinute.toString().length === 1
            ? `0${row.startMinute}`
            : row.startMinute}
        </div>
      ),
    },
    {
      name: t("pages.default.homepage.table.endTime"),
      sselector: "endHour",
      sortable: true,
      cell: (row) => (
        <div>
          {row.endHour.toString().length === 1
            ? `0${row.endHour}`
            : row.endHour}
          :
          {row.endMinute.toString().length === 1
            ? `0${row.endMinute}`
            : row.endMinute}
        </div>
      ),
    },
    {
      name: t("pages.default.certificates.table.status"),
      selector: "yekgProcessStatus",
      sortable: true,
      cell: (row) => (
        <Pill
          label={t(`ui.default.${row.yekgProcessStatus}`)}
          type={yekgProcessStatusTypes[row.yekgProcessStatus]}
        />
      ),
    },
  ];

  const organizedMarketColumns = [
    {
      name: t("pages.default.homepage.table.contract"),
      selector: "contractName",
      sortable: true,
    },
    {
      name: t("pages.default.homepage.table.averagePrice"),
      selector: "averagePrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({
          num: row.averagePrice || 0,
          digit: 2,
        });
      },
    },
    {
      name: t("pages.default.homepage.table.matchAmount"),
      selector: "totalAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.totalAmount);
      },
    },
  ];

  const systemAndErrorMsgsColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.homepage.table.msgType"),
      selector: "auditType",
      sortable: true,
      cell: (row) => translations[row.auditType],
    },
    {
      name: t("pages.default.homepage.table.userName"),
      selector: "userName",
      sortable: true,
    },
    {
      name: t("pages.default.homepage.table.time"),
      selector: "createDate",
      sortable: true,
      cell: (row) => moment(row.createDate).format("DD.MM.YYYY HH:mm:ss"),
    },
    {
      name: t("pages.default.homepage.table.log"),
      selector: "description",
      sortable: true,
      showTitle: true,
    },
  ];

  const objectionsColumns = [
    {
      name: t("pages.default.certificates.table.organizationName"),
      selector: "organizationName",
      sortable: true,
      showTitle: true,
    },
    {
      name: t("pages.default.homepage.table.userName"),
      selector: "userName",
      sortable: true,
    },
    {
      name: t("pages.default.homepage.table.contract"),
      selector: "contractName",
      sortable: true,
    },
    {
      name: t("pages.default.homepage.table.bidId"),
      selector: "bidId",
      sortable: true,
    },
    {
      name: t("pages.default.homepage.table.objectionTime"),
      selector: "createdAt",
      sortable: true,
    },
    {
      name: t("pages.default.homepage.table.answerTime"),
      selector: "answeredAt",
      sortable: true,
    },
  ];

  useEffect(() => {
    dispatch(getMarketProcessesListRequest());
    dispatch(getOrganizedMarketInfoRequest());
    dispatch(getSimplifiedSessionsRequested());
    handleFetchSystemMsgs(
      activePage,
      sortedColumn,
      sortDirection.toUpperCase()
    );
    dispatch(getHomeObjectionsListRequest());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    selectedTab.onClick();
  }, [selectedTab]);

  useEffect(() => {
    if (sessions != null && sessions.length > 0) {
      dispatch(getSessionRequested({ sessionId: sessions[0].value }));
    }
  }, [sessions]);

  useEffect(() => {
    dispatch(getChartDataRequest(year, month));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  useEffect(() => {
    if (currentContracts) {
      const totalAmount = currentContracts.reduce(
        (acc, current) => (acc += current.totalAmount ?? 0),
        0
      );
      setTotalMatchedAmount(totalAmount);
    }
  }, [currentContracts]);

  useEffect(() => {
    // set Period options
    let d = new Date();
    let filterPeriods = [];
    for (let i = 0; i <= 11; i++) {
      const name =
        t(`ui.default.months.${moment().subtract(i, "month").format("M")}`) +
        " " +
        moment().subtract(i, "month").format("YYYY");
      filterPeriods.push({
        name,
        value: moment().subtract(i, "month").format("YYYY-M"),
      });
      d.setMonth(d.getMonth() - 1);
    }
    setFilterPeriodOptions(filterPeriods);
  }, [t]);

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
    list.length !== 0 && dispatch(clearGeneralOperationsList());
  };

  const filterSession = (e) => {
    dispatch(getSessionRequested({ sessionId: e.target.value }));
  };

  const handleChartTermChange = (e) => {
    dispatch(setChartTerm(e.target.value));
  };

  const handlePrepareChartData = () => {
    const chartDataLabels = {
      exportCount: t("pages.default.homepage.tabs.export").toUpperCase(),
      redemptionCount: t("ui.default.codes.REDEMPTION").toUpperCase(),
      cancelCount: t("ui.default.codes.CANCEL").toUpperCase(),
      repealCount: t("ui.default.codes.REPEAL").toUpperCase(),
    };

    let labels = [];
    let dataSet = [];

    for (let key in data) {
      labels.push(chartDataLabels[key]);
      dataSet.push(data[key]);
    }

    return {
      labels,
      datasets: [
        {
          label: t("pages.default.homepage.chart.documentAmount"),
          data: dataSet,
          backgroundColor: "#2684ff",
        },
      ],
    };
  };

  const handleFetchSystemMsgs = (activePage, selector, direction) => {
    dispatch(
      getSystemMsgListRequest({
        fieldName: fieldNames[selector],
        sortType: direction,
        page: activePage,
        size,
      })
    );
  };

  const isRowUnanswered = (row) => !row.answeredAt;

  return (
    <>
      <h5 className="km__u--text-has-emphasis km__p--homepage-header-container">
        {t("pages.default.homepage.title")}
        <div className="km__p--homepage-switch-container">
          <ToggleSwitch
            label={t("pages.default.homepage.organizedMarket")}
            checked={false}
            onClick={() => history.push("/offer-operations")}
            readOnly
            id="home-toggle"
          />
        </div>
      </h5>

      <div className="km__p--homepage-row-container">
        <div>
          <div className="km__p--card km__p--card-home">
            <div className="km__p--homepage-header">
              <div className="km__u--text_title">
                {t("pages.default.homepage.marketProcesses.title")}
              </div>
            </div>
            {!loadingProcess && (
              <DataTable columns={marketProcessesColumns} data={listProcess} />
            )}
            {!loadingProcess && listProcess.length === 0 && (
              <EmptyState
                icon="file-text"
                text={t("pages.default.homepage.marketProcesses.notFound")}
              />
            )}
            {loadingProcess && (
              <Loading
                text={t("pages.default.homepage.marketProcesses.loading")}
              />
            )}
          </div>
        </div>
        <div>
          <div className="km__p--card km__p--card-home">
            <ul className="km__p--homepage-tab-list">
              {generalOperationsTabs.map((tab, index) => (
                <li
                  key={index}
                  className={`km__u--text_title ${
                    tab.title === selectedTab.title
                      ? "km__p--homepage-tab-active"
                      : ""
                  }`}
                  onClick={() => handleTabClick(tab)}
                >
                  {tab.title}
                </li>
              ))}
            </ul>
            {!loadingGO && (
              <DataTable
                columns={selectedTab.columns}
                data={list}
                rowKey="id"
              />
            )}
            {!loadingGO && list.length === 0 && (
              <EmptyState icon="file-text" text={selectedTab.notFound} />
            )}
            {loadingGO && <Loading text={selectedTab.loading} />}
          </div>
        </div>
      </div>

      <div className="km__p--homepage-row-container">
        <div>
          <div className="km__p--card km__p--card-home">
            <div className="km__p--homepage-header">
              <div className="km__u--text_title">
                {t("pages.default.homepage.periodicGeneralOperations")}
              </div>
              <Select
                options={filterPeriodOptions}
                name="period"
                defaultValue={`${year}-${month}`}
                onChange={handleChartTermChange}
                id="home-select-period"
              />
            </div>
            {!loadingChart && (
              <div className="chart-container">
                <Bar
                  data={handlePrepareChartData}
                  options={{
                    maintainAspectRatio: false,
                    type: "bar",
                    scales: {
                      yAxes: [
                        {
                          stacked: true,
                          // id: "first-y-axis",
                          // position: "left",
                          // type: "logarithmic",
                          ticks: {
                            suggestedMin: 1,
                            suggestedMax: 20,
                            // callback: function (value, index, values) {
                            //   if (value > 1000000) return "100M";
                            //   if (value === 1000000) return "1M";
                            //   if (value === 100000) return "100K";
                            //   if (value === 10000) return "10K";
                            //   if (value === 1000) return "1K";
                            //   if (value === 100) return "100";
                            //   if (value === 10) return "10";
                            //   if (value === 0) return "0";
                            //   return null;
                            // },
                          },
                        },
                      ],
                      xAxes: [
                        {
                          barThickness: 20,
                          maxBarThickness: 20,
                          stacked: true,
                        },
                      ],
                    },
                  }}
                />
              </div>
            )}
            {loadingChart && <Loading />}
          </div>
        </div>
        <div>
          <div className="km__p--card km__p--card-home">
            <div
              className="km__p--homepage-header km__u--text_title"
              style={{
                height: 70,
              }}
            >
              <div>
                {t("pages.default.homepage.organizedMarketInformation")}
              </div>
              {sessions && (
                <Select
                  options={sessions}
                  name="sessionDate"
                  defaultValue={sessions[sessions.length - 1]}
                  onChange={filterSession}
                />
              )}
            </div>
            {!loadingInfo && (
              <DataTable
                columns={organizedMarketColumns}
                data={currentContracts || []}
                rowKey="id"
              />
            )}
            {loadingInfo && <Loading />}
            <div className="km__p--homepage-total-container km__u--text_title">
              {t("pages.default.homepage.totalMatchAmount")}:{" "}
              {convertAmountToFrontend(totalMatchedAmount)}
            </div>
          </div>
        </div>
      </div>

      <div className="km__p--card km__p--card-home">
        <div className="km__p--homepage-header">
          <div className="km__u--text_title">
            {t("pages.default.homepage.systemAndErrorMsgs.title")}
          </div>
        </div>
        <DataTableServerSideRendering
          columns={systemAndErrorMsgsColumns}
          data={listMsg}
          rowKey="id"
          sortedColumn={sortedColumn}
          setSortedColumn={setSortedColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          pagination
          activePage={activePage}
          totalPageCount={totalPageCount}
          handleFetchData={handleFetchSystemMsgs}
        />
        {!loadingMsg && listMsg.length === 0 && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.homepage.systemAndErrorMsgs.notFound")}
          />
        )}
      </div>

      <div className="km__p--card km__p--card-home">
        <div className="km__p--homepage-header">
          <div className="km__u--text_title">
            {t("pages.default.homepage.objections.title")}
          </div>
        </div>
        {!loadingObjections && (
          <DataTableBasic
            columns={objectionsColumns}
            data={listObjections}
            rowKey="bidId"
            blueRowDirection
            isRowBlue={isRowUnanswered}
          />
        )}
        {!loadingObjections && listObjections.length === 0 && (
          <EmptyState
            icon="file-text"
            text={t("pages.default.homepage.objections.notFound")}
          />
        )}
        {loadingObjections && (
          <Loading text={t("pages.default.homepage.objections.loading")} />
        )}
      </div>
    </>
  );
};

export default Homepage;
